import { Injectable } from '@angular/core';
import { generateClient } from 'aws-amplify/api';
import * as queries from '@graphql/queries';
import * as mutations from '@graphql/mutations';
import {
    CreateLandingCategoryHighlightInput,
    DeleteLandingCategoryHighlightInput,
    LandingCategoryHighlight,
    ModelLandingCategoryHighlightFilterInput,
    UpdateLandingCategoryHighlightInput
} from '@app/core/services/api/API';

@Injectable({
    providedIn: 'root'
})
export class LandingCategoryHighlightService {
    private amplifyClient = generateClient();
    private _cachedItems: Map<string, LandingCategoryHighlight[]> = new Map();
    private _isLoading: Map<string, boolean> = new Map();
    private _loadingPromises: Map<
        string,
        Promise<LandingCategoryHighlight[]>
    > = new Map();

    private _getCacheKey(
        filter: ModelLandingCategoryHighlightFilterInput
    ): string {
        return JSON.stringify(filter || {});
    }

    async listLandingCategoryHighlights(
        filter: ModelLandingCategoryHighlightFilterInput = null
    ): Promise<LandingCategoryHighlight[]> {
        const cacheKey = this._getCacheKey(filter);

        if (this._cachedItems.has(cacheKey)) {
            return this._cachedItems.get(cacheKey);
        }

        if (
            this._isLoading.get(cacheKey) &&
            this._loadingPromises.has(cacheKey)
        ) {
            return this._loadingPromises.get(cacheKey);
        }

        this._isLoading.set(cacheKey, true);
        const promise = this._fetchItems(filter, cacheKey);
        this._loadingPromises.set(cacheKey, promise);

        return promise;
    }

    private async _fetchItems(
        filter: ModelLandingCategoryHighlightFilterInput,
        cacheKey: string
    ): Promise<LandingCategoryHighlight[]> {
        try {
            const data = (
                await this.amplifyClient.graphql({
                    query: queries.listLandingCategoryHighlights,
                    variables: { filter }
                })
            ).data.listLandingCategoryHighlights.items;

            if (data) {
                const items = data as LandingCategoryHighlight[];
                this._cachedItems.set(cacheKey, items);
                this._isLoading.set(cacheKey, false);
                this._loadingPromises.delete(cacheKey);
                return items;
            }
            this._isLoading.set(cacheKey, false);
            this._loadingPromises.delete(cacheKey);
            return [];
        } catch (error) {
            console.error(
                '[LandingCategoryHighlight] listLandingCategoryHighlights failed:',
                error
            );
            this._isLoading.set(cacheKey, false);
            this._loadingPromises.delete(cacheKey);
            return [];
        }
    }

    clearCache(): void {
        this._cachedItems.clear();
        this._isLoading.clear();
        this._loadingPromises.clear();
    }

    async getLandingCategoryHighlight(
        id: string
    ): Promise<LandingCategoryHighlight> {
        try {
            const data = (
                await this.amplifyClient.graphql({
                    query: queries.getLandingCategoryHighlight,
                    variables: { id }
                })
            ).data.getLandingCategoryHighlight;
            if (data) {
                return data as LandingCategoryHighlight;
            }
        } catch (error) {
            console.error('Error finding LandingCategoryHighlight:', error);
        }
        return null;
    }

    async createLandingCategoryHighlight(
        input: CreateLandingCategoryHighlightInput
    ): Promise<LandingCategoryHighlight> {
        try {
            const data = (
                await this.amplifyClient.graphql({
                    query: mutations.createLandingCategoryHighlight,
                    variables: { input }
                })
            ).data.createLandingCategoryHighlight;
            if (data) {
                this.clearCache();
                return data as LandingCategoryHighlight;
            }
        } catch (error) {
            console.error('Error creating LandingCategoryHighlight:', error);
        }
        return null;
    }

    async updateLandingCategoryHighlight(
        input: UpdateLandingCategoryHighlightInput
    ): Promise<LandingCategoryHighlight> {
        try {
            const data = (
                await this.amplifyClient.graphql({
                    query: mutations.updateLandingCategoryHighlight,
                    variables: { input }
                })
            ).data.updateLandingCategoryHighlight;
            if (data) {
                this.clearCache();
                return data as LandingCategoryHighlight;
            }
        } catch (error) {
            console.error('Error updating LandingCategoryHighlight:', error);
        }
        return null;
    }

    async deleteLandingCategoryHighlight(
        input: DeleteLandingCategoryHighlightInput
    ): Promise<LandingCategoryHighlight> {
        try {
            const data = (
                await this.amplifyClient.graphql({
                    query: mutations.deleteLandingCategoryHighlight,
                    variables: { input }
                })
            ).data.deleteLandingCategoryHighlight;
            if (data) {
                this.clearCache();
                return data as LandingCategoryHighlight;
            }
        } catch (error) {
            console.error('Error deleting LandingCategoryHighlight:', error);
        }
        return null;
    }
}
